import { Input } from "@intility/bifrost-react";
import Select from "@intility/bifrost-react-select";
import { useState } from "react";
import { isKnownStack, KNOWN_STACKS } from "../lib/clientTypes";

// The stack picker (ADR-0060 decision 6): what the client's application is
// built with. The known options carry a dedicated SDK guide; "Other" takes any
// value, which the API stores as written and displays without a guide. Only
// the open "Other" input is local state: the caller owns the value, so the
// picker serves both the wizard (local state, sent on create) and the
// quickstart (sent with each pick).
const NOT_SET = { value: "", label: "Not set" };
const OTHER = { value: "__other__", label: "Other…" };

export function ClientStackPicker({
  stack,
  onPick,
  disabled = false,
}: {
  /** The current stack, or null when none is recorded. */
  stack: string | null;
  /** The picked stack, or null when cleared back to none. */
  onPick: (stack: string | null) => void;
  disabled?: boolean;
}) {
  // "Other" is open while the current value is neither known nor empty, or
  // from the moment it is chosen; its input starts from the current value.
  const other = stack != null && !isKnownStack(stack);
  const [otherOpen, setOtherOpen] = useState(other);
  const [otherText, setOtherText] = useState(other ? stack : "");

  function pick(value: string) {
    if (value === "") {
      setOtherOpen(false);
      onPick(null);
      return;
    }
    if (value === OTHER.value) {
      setOtherOpen(true);
      return;
    }
    setOtherOpen(false);
    onPick(value);
  }

  // The "Other" value commits on blur or Enter, not per keystroke.
  function commitOther() {
    const trimmed = otherText.trim();
    if (trimmed) onPick(trimmed);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Select
        label="Stack"
        hideLabel
        options={[NOT_SET, ...KNOWN_STACKS, OTHER]}
        value={otherOpen ? OTHER : knownOption(stack)}
        onChange={(option) => option && pick(option.value)}
        isDisabled={disabled}
      />
      {otherOpen && (
        <Input
          label="Other stack"
          hideLabel
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          onBlur={commitOther}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitOther();
            }
          }}
          placeholder="e.g. python"
          disabled={disabled}
        />
      )}
    </div>
  );
}

// The option a known stack selects, or "Not set" for anything else. The value
// is matched case-insensitively, the way the API normalizes on write.
function knownOption(stack: string | null) {
  return KNOWN_STACKS.find((o) => o.value === stack?.toLowerCase()) ?? NOT_SET;
}
